import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Globe2,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Mail,
  MessageSquare,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../config";
import BannerCoverageWidget from "../components/admin/BannerCoverageWidget";

// Country flag emoji map — same convention used by SocChangesWidget.
const FLAGS = {
  IT: "\u{1F1EE}\u{1F1F9}", FR: "\u{1F1EB}\u{1F1F7}", ES: "\u{1F1EA}\u{1F1F8}",
  DE: "\u{1F1E9}\u{1F1EA}", UK: "\u{1F1EC}\u{1F1E7}", PT: "\u{1F1F5}\u{1F1F9}",
  BE: "\u{1F1E7}\u{1F1EA}", NL: "\u{1F1F3}\u{1F1F1}", PL: "\u{1F1F5}\u{1F1F1}",
  SE: "\u{1F1F8}\u{1F1EA}", NO: "\u{1F1F3}\u{1F1F4}", FI: "\u{1F1EB}\u{1F1EE}",
  DK: "\u{1F1E9}\u{1F1F0}", CZ: "\u{1F1E8}\u{1F1FF}", GR: "\u{1F1EC}\u{1F1F7}",
  CH: "\u{1F1E8}\u{1F1ED}", GBZ: "\u{1F1EC}\u{1F1EE}", IRL: "\u{1F1EE}\u{1F1EA}",
};
const flag = (c) => FLAGS[c] || "\u{1F310}";

const ChannelIcon = ({ channel }) =>
  channel === "sms" ? <MessageSquare size={14} /> : <Mail size={14} />;

export default function I18nStatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/i18n-status`);
      setData(r.data);
    } catch (e) {
      toast.error(`Errore caricamento: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copyFingerprint = (fp) => {
    navigator.clipboard?.writeText(fp);
    toast.success(`Fingerprint ${fp} copiato`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="i18n-status-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-lojack-blue-600/15 border border-lojack-blue-500/40 flex items-center justify-center">
              <Globe2 size={22} className="text-lojack-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Stato salute i18n</h1>
              <p className="text-gray-400 text-sm mt-1">
                Coverage dei catalog hi di traduzione customer-facing su tutti i 18 paesi supportati. Read-only.
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-[#1A1A1A] border border-gray-700 hover:border-lojack-blue-500 text-gray-300 hover:text-white transition disabled:opacity-50"
            data-testid="i18n-refresh-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Aggiorna
          </button>
        </div>

        {loading && !data && (
          <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
            <Loader2 size={20} className="animate-spin" /> Caricamento catalog hi…
          </div>
        )}

        {data && (
          <>
            {/* Overall summary */}
            <div
              className={`rounded-2xl border p-5 mb-8 flex items-center gap-4 ${
                data.overall_healthy
                  ? "bg-emerald-950/20 border-emerald-700/40"
                  : "bg-lojack-clause-950/20 border-lojack-clause-700/40"
              }`}
              data-testid="i18n-overall-summary"
            >
              {data.overall_healthy ? (
                <CheckCircle2 size={32} className="text-emerald-400 flex-shrink-0" />
              ) : (
                <AlertTriangle size={32} className="text-lojack-clause-400 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h2 className="text-lg font-semibold">
                  {data.overall_healthy
                    ? `Tutti i ${data.total_catalogues} catalog hi sono al 100%`
                    : `${data.unhealthy_catalogues} catalog ho/i con problemi`}
                </h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  {data.supported_countries_count} paesi supportati · {data.healthy_catalogues}/{data.total_catalogues} catalog hi sani
                </p>
              </div>
              <div className="text-right text-xs text-gray-500 hidden md:block">
                <p className="font-mono">{data.supported_countries.join(" · ")}</p>
              </div>
            </div>

            {/* Banner coverage widget — Fabrizio CR 01/03/2026 */}
            <BannerCoverageWidget />

            {/* Catalogue cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="catalogue-grid">
              {data.catalogues.map((cat) => (
                <div
                  key={cat.module}
                  className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden flex flex-col"
                  data-testid={`catalogue-card-${cat.module}`}
                >
                  {/* Top bar */}
                  <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-800 bg-[#0F0F0F]">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-[#1A1A1A] border border-gray-700 flex items-center justify-center text-gray-300 flex-shrink-0">
                        <ChannelIcon channel={cat.channel} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold truncate">{cat.name}</h3>
                        <p className="text-[11px] text-gray-500 font-mono truncate" title={cat.module}>
                          {cat.module}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {cat.healthy ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400 font-mono bg-emerald-900/30 border border-emerald-700/40 rounded-full px-2 py-0.5">
                          <CheckCircle2 size={10} /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-lojack-clause-400 font-mono bg-lojack-clause-900/30 border border-lojack-clause-700/40 rounded-full px-2 py-0.5">
                          <AlertTriangle size={10} /> {cat.issues.length} issue
                        </span>
                      )}
                      <span className="text-[11px] text-gray-500">{cat.coverage_pct}% cov</span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-5 space-y-4 flex-1">
                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Nativi</p>
                        <p className="text-xl font-bold text-white mt-0.5">{cat.native_countries.length}</p>
                      </div>
                      <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Alias</p>
                        <p className="text-xl font-bold text-white mt-0.5">{Object.keys(cat.aliases).length}</p>
                      </div>
                      <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Chiavi/pack</p>
                        <p className="text-xl font-bold text-white mt-0.5">
                          {cat.key_count_per_pack || <span className="text-gray-600 text-base">—</span>}
                        </p>
                      </div>
                    </div>

                    {/* Natives chip cloud */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Pack nativi</p>
                      <div className="flex flex-wrap gap-1.5" data-testid={`natives-${cat.module}`}>
                        {cat.native_countries.map((c) => (
                          <span
                            key={c}
                            className="inline-flex items-center gap-1 text-xs bg-[#1A1A1A] border border-gray-700 rounded-md px-2 py-0.5 font-mono text-gray-300"
                          >
                            <span>{flag(c)}</span>
                            <span>{c}</span>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Aliases */}
                    {Object.keys(cat.aliases).length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Alias runtime</p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(cat.aliases).map(([alias, target]) => (
                            <span
                              key={alias}
                              className="inline-flex items-center gap-1 text-xs bg-lojack-blue-900/20 border border-lojack-blue-700/40 rounded-md px-2 py-0.5 font-mono text-lojack-blue-300"
                              title={`${alias} → ${target}`}
                            >
                              {flag(alias)} {alias} → {target}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Issues */}
                    {cat.issues.length > 0 && (
                      <div className="bg-lojack-clause-950/30 border border-lojack-clause-700/40 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-lojack-clause-400 mb-2 font-semibold">
                          Anomalie rilevate
                        </p>
                        <ul className="text-xs text-lojack-clause-200 space-y-1 list-disc pl-4">
                          {cat.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Footer with fingerprint */}
                  <div className="px-5 py-3 border-t border-gray-800 bg-[#0F0F0F] flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500">
                      Fingerprint catalogo
                    </span>
                    <button
                      onClick={() => copyFingerprint(cat.fingerprint)}
                      className="inline-flex items-center gap-1.5 font-mono text-xs text-gray-300 hover:text-lojack-blue-300 transition"
                      title="Copia fingerprint"
                      data-testid={`fingerprint-${cat.module}`}
                    >
                      {cat.fingerprint}
                      <Copy size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Helper note */}
            <p className="text-gray-500 text-xs mt-8 leading-relaxed max-w-3xl">
              I 4 paesi <span className="font-mono">CH / BE / GBZ / IRL</span> sono coperti via alias runtime
              (CH→DE, BE→FR, GBZ/IRL→UK) — design intenzionale che riduce la manutenzione delle traduzioni
              senza sacrificare la copertura GDPR Art. 12(1). Il fingerprint cambia ogni volta che un pack
              viene modificato e può essere abbinato a quello stampato sulla cover del PDF audit DPO.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
